import * as React from 'react'
import { FC, useEffect, useState } from 'react'
import { useAppDispatch } from '@user/store/hooks'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Skeleton from '@mui/material/Skeleton'
import Alert from '@mui/material/Alert'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { fetchTerritoryPreview } from '@user/store/territory/api'
import {
  TerritoryBoundary,
  TerritoryPerformance,
} from '@user/store/territory/typings'
import { METRICS } from './territoryMetrics'

type Props = {
  /** The shape being drawn or redrawn. */
  boundary: TerritoryBoundary
  /**
   * The territory's CURRENT shape, when this is a redraw. Present means "show before and
   * after"; absent means a brand-new territory with nothing to compare against.
   */
  previousBoundary?: TerritoryBoundary | null
}

/** A signed change, coloured only by direction -- more is not always better. */
const Delta: FC<{
  before: number
  after: number
  format: (v: number) => string
}> = ({ before, after, format }) => {
  const diff = after - before
  if (Math.abs(diff) < 0.5) return null

  return (
    <Typography
      variant="caption"
      sx={{ color: diff > 0 ? 'success.main' : 'warning.main', ml: 0.5 }}
    >
      {diff > 0 ? '+' : '−'}
      {format(Math.abs(diff))}
    </Typography>
  )
}

/**
 * What the shape being drawn would contain, shown before it is saved (WWM-17508).
 *
 * The point is to make rebalancing a decision rather than a guess. Splitting a territory down
 * the middle looks reasonable and can leave 3 contacts on one side and 109 on the other -- which
 * is what happens with a real territory in this data. Seeing that while the line is still a
 * draft costs one request; discovering it weeks later costs a redraw and a rep's quarter.
 *
 * For a redraw it shows before and after, because the question is never "what is in this shape"
 * but "what moves if I accept it".
 *
 * Fires once per completed stroke, not continuously while dragging: a request per pointermove
 * would need throttling and cancellation to avoid showing the answer to a shape the user has
 * already moved past.
 */
export const TerritoryPreviewPanel: FC<Props> = ({
  boundary,
  previousBoundary,
}) => {
  const dispatch = useAppDispatch()

  const [after, setAfter] = useState<TerritoryPerformance | null>(null)
  const [before, setBefore] = useState<TerritoryPerformance | null>(null)
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false

    const run = async () => {
      setLoading(true)
      setFailed(false)

      const [afterResult, beforeResult] = await Promise.all([
        dispatch(fetchTerritoryPreview(boundary)),
        previousBoundary
          ? dispatch(fetchTerritoryPreview(previousBoundary))
          : Promise.resolve(null),
      ])

      if (cancelled) return

      if (!afterResult || fetchTerritoryPreview.rejected.match(afterResult)) {
        setFailed(true)
        setLoading(false)
        return
      }

      setAfter(afterResult.payload)
      // A failed "before" is not fatal -- the after figures are still worth showing, just
      // without the comparison.
      setBefore(
        beforeResult && fetchTerritoryPreview.fulfilled.match(beforeResult)
          ? beforeResult.payload
          : null,
      )
      setLoading(false)
    }

    run()
    return () => {
      cancelled = true
    }
  }, [dispatch, boundary, previousBoundary])

  if (failed) {
    // Soft-fail: the preview is advice, and losing it must never block saving the territory.
    return (
      <Alert
        severity="info"
        sx={{ mb: 2 }}
        data-test-id="territory-preview-failed"
      >
        Could not work out what this area contains. You can still save it.
      </Alert>
    )
  }

  return (
    <Paper
      variant="outlined"
      sx={{ p: 1.5, mb: 2, bgcolor: 'action.hover' }}
      data-test-id="territory-preview"
    >
      <Typography variant="caption" color="text.secondary">
        {previousBoundary
          ? 'What changes if you save this shape'
          : 'What this area contains'}
        {' · activity over the last 12 months'}
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 1,
          mt: 1,
        }}
      >
        {METRICS.map((metric) => (
          <Box key={metric.label}>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              {metric.label}
            </Typography>

            {loading || !after ? (
              <Skeleton width={48} height={20} />
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                {before && (
                  <>
                    <Typography variant="body2" color="text.disabled">
                      {metric.format(metric.value(before))}
                    </Typography>
                    <ArrowForwardIcon
                      sx={{ fontSize: 12, mx: 0.3, color: 'text.disabled' }}
                    />
                  </>
                )}
                <Typography variant="body2" fontWeight={600}>
                  {metric.format(metric.value(after))}
                </Typography>
                {before && (
                  <Delta
                    before={metric.value(before)}
                    after={metric.value(after)}
                    format={metric.format}
                  />
                )}
              </Box>
            )}
          </Box>
        ))}
      </Box>

      {(before?.isPartialCount || after?.isPartialCount) && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 1 }}
        >
          Counts stopped at the server&apos;s limit, so these are minimums — the
          real figures are higher.
        </Typography>
      )}
    </Paper>
  )
}
